import type { TourScrollStrategy } from '@app/shared/tour';

const TOUR_SCROLL_MS = 550;
const TOUR_SCROLL_PADDING_PX = 16;
const TOUR_SCROLL_IDLE_MS = 80;
const TOUR_SCROLL_START_GRACE_MS = 120;
const TOUR_SCROLL_MAX_MS = 900;

let homeScrollAnimToken = 0;

const getHomeScrollport = (): HTMLElement | null =>
  document.querySelector('[data-home-scrollport]');

const RECENT_UPDATES_ACTION_STEP_IDS = new Set([
  'recent-updates-reactions',
  'recent-updates-comments',
]);

const getTourViewportTarget = (
  element: Element | undefined,
  stepId?: string
): Element | null => {
  if (stepId === 'spaces') {
    return document.querySelector('#home-space-widget');
  }
  if (stepId?.startsWith('space-') && element) {
    return element.closest('article') ?? element;
  }
  if (stepId && RECENT_UPDATES_ACTION_STEP_IDS.has(stepId)) {
    // Use the resolved (in-view) CTA's card for page scroll, not always the first.
    return (
      (element instanceof Element
        ? element.closest('[role="listitem"]')
        : null) ??
      document.querySelector('#home-recent-updates-widget') ??
      element ??
      null
    );
  }
  return element ?? null;
};

const isElementFittedInHomeView = (
  element: Element | null | undefined,
  scrollport: HTMLElement | null
): boolean => {
  if (!(element instanceof HTMLElement)) {
    return true;
  }

  const rect = element.getBoundingClientRect();
  const port = scrollport?.getBoundingClientRect() ?? {
    top: 0,
    bottom: window.innerHeight,
    height: window.innerHeight,
  };
  const portHeight = port.height ?? port.bottom - port.top;
  const padding = TOUR_SCROLL_PADDING_PX;

  if (rect.height > portHeight - padding * 2) {
    return rect.top >= port.top - 2 && rect.top <= port.top + padding * 2;
  }

  return rect.top >= port.top - 2 && rect.bottom <= port.bottom + 2;
};

const getHomeScrollTopForTarget = (
  target: HTMLElement,
  scrollport: HTMLElement
): number => {
  const targetRect = target.getBoundingClientRect();
  const portRect = scrollport.getBoundingClientRect();
  const padding = TOUR_SCROLL_PADDING_PX;

  const delta =
    targetRect.height > portRect.height - padding * 2
      ? targetRect.top - (portRect.top + padding)
      : targetRect.top +
        targetRect.height / 2 -
        (portRect.top + portRect.height / 2);

  return Math.max(0, scrollport.scrollTop + delta);
};

const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;

const animateHomeScrollportTo = (
  scrollport: HTMLElement,
  top: number,
  durationMs = TOUR_SCROLL_MS
): void => {
  const from = scrollport.scrollTop;
  const target = Math.max(0, top);
  const delta = target - from;
  if (Math.abs(delta) < 1) {
    return;
  }

  const token = ++homeScrollAnimToken;
  const startedAt = performance.now();

  const frame = (now: number) => {
    if (token !== homeScrollAnimToken) {
      return;
    }
    const progress = Math.min(1, (now - startedAt) / durationMs);
    scrollport.scrollTop = from + delta * easeInOutCubic(progress);
    if (progress < 1) {
      window.requestAnimationFrame(frame);
    }
  };

  window.requestAnimationFrame(frame);
};

const scrollSpaceCarouselToElement = (element: Element | undefined): void => {
  if (!(element instanceof HTMLElement)) {
    return;
  }

  const slide = element.closest('.swiper-slide') as HTMLElement | null;
  const wrapper = slide?.closest('.swiper-wrapper') as HTMLElement | null;
  const container = wrapper?.closest('.swiper') as HTMLElement | null;

  if (!slide || !container) {
    return;
  }

  const swiper = (container as any)?.swiper;
  if (!swiper || typeof swiper.slideTo !== 'function') {
    return;
  }

  const slides = Array.from(wrapper!.children);
  const slideIndex = slides.indexOf(slide);
  if (slideIndex < 0) {
    return;
  }

  const containerRect = container.getBoundingClientRect();
  const slideRect = slide.getBoundingClientRect();
  const isFullyVisible =
    slideRect.left >= containerRect.left - 2 &&
    slideRect.right <= containerRect.right + 2;

  if (!isFullyVisible) {
    swiper.slideTo(slideIndex, TOUR_SCROLL_MS);
  }
};

/**
 * Recent Updates uses Virtuoso — reaction/comment targets are on cards.
 * If the feed was scrolled, the first card's CTA can sit outside the feed
 * clip. Reset to the start (instant — SnapScroller uses scroll-behavior:smooth)
 * so the tour can highlight the first card.
 */
export const scrollRecentUpdatesFeedToStart = (): boolean => {
  const scroller = document.querySelector(
    '[data-tour="home-recent-updates-scroller"]'
  );
  if (!(scroller instanceof HTMLElement)) {
    return false;
  }

  const needsScroll = scroller.scrollTop > 1 || scroller.scrollLeft > 1;
  if (!needsScroll) {
    return false;
  }

  // Override smooth scroll-behavior so Virtuoso remounts before highlight measure.
  const prevBehavior = scroller.style.scrollBehavior;
  scroller.style.scrollBehavior = 'auto';
  scroller.scrollTop = 0;
  scroller.scrollLeft = 0;
  scroller.style.scrollBehavior = prevBehavior;
  return true;
};

export const scrollHomeToTourTarget: TourScrollStrategy = (element, stepId) => {
  if (stepId?.startsWith('space-') && stepId !== 'spaces') {
    scrollSpaceCarouselToElement(element);
  }

  // Recent-updates reaction/comment feed positioning is handled by
  // resolveHomeRecentUpdatesTourElement (visible CTA or scroll-to-start).

  const target = getTourViewportTarget(element, stepId);
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const scrollport = getHomeScrollport();
  if (isElementFittedInHomeView(target, scrollport)) {
    return false;
  }

  if (!scrollport) {
    target.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: 'smooth',
    });
    return true;
  }

  animateHomeScrollportTo(
    scrollport,
    getHomeScrollTopForTarget(target, scrollport),
    TOUR_SCROLL_MS
  );
  return true;
};

export const scrollHomeToTop = (): void => {
  const scrollport = getHomeScrollport();
  if (!scrollport || scrollport.scrollTop < 1) {
    return;
  }
  animateHomeScrollportTo(scrollport, 0, TOUR_SCROLL_MS);
};

export const cancelHomeScrollAnim = (): void => {
  homeScrollAnimToken += 1;
};

export const waitForHomeScrollSettle = (
  scrollport: HTMLElement | null
): Promise<void> =>
  new Promise((resolve) => {
    if (!scrollport) {
      resolve();
      return;
    }

    let settled = false;
    let seenScroll = false;
    let idleId: number | null = null;

    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      if (idleId != null) {
        window.clearTimeout(idleId);
      }
      window.clearTimeout(maxId);
      window.clearTimeout(graceId);
      scrollport.removeEventListener('scroll', onScroll);
      scrollport.removeEventListener('scrollend', finish);
      resolve();
    };

    const onScroll = () => {
      seenScroll = true;
      if (idleId != null) {
        window.clearTimeout(idleId);
      }
      idleId = window.setTimeout(finish, TOUR_SCROLL_IDLE_MS);
    };

    const maxId = window.setTimeout(finish, TOUR_SCROLL_MAX_MS);
    const graceId = window.setTimeout(() => {
      if (!seenScroll) {
        finish();
      }
    }, TOUR_SCROLL_START_GRACE_MS);

    scrollport.addEventListener('scroll', onScroll, { passive: true });
    scrollport.addEventListener('scrollend', finish, { once: true });
  });
